import {
  AxiosHeaders,
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import { readHeader, setHeader } from 'api/httpHeaders';
import { isRecord } from 'utils/typeGuards';

import {
  CANDIDATE_CAPABILITY_HEADER,
  CANDIDATE_CAPABILITY_INVALID_DETAIL,
  type CandidateContext,
  ensureCapability,
  captureCandidateContext,
  getCandidateSession,
  invalidateCapabilityToken,
  isCapabilityMintUrl,
  peekCapability,
} from './candidateCapability';
import { mapCandidateRequestUrl } from './requestTransport';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Set on the single retry after the server rejected a candidate capability. */
    candidateCapabilityRetry?: boolean;
    /** Captured when a request starts; retries must never consult a newer route. */
    candidateContext?: CandidateContext | null;
  }
}

let lastMissingInterviewHeaderRumAtMs = 0;
function maybeLogMissingInterviewHeader(config: InternalAxiosRequestConfig) {
  // Only called when no candidate credential could be derived for the request.
  try {
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '';
    const isCandidateRoute =
      pathname.startsWith('/web-interview') || pathname.startsWith('/web-call');
    if (!isCandidateRoute) return;

    const url: string = config?.url || '';
    const isLinkCheck = url.includes('/check_web_link');
    if (!isLinkCheck) return;

    const now = Date.now();
    if (now - lastMissingInterviewHeaderRumAtMs < 60_000) return;
    lastMissingInterviewHeaderRumAtMs = now;

    datadogRum.addAction('missing_interview_header', {
      pathname,
      url,
    });
  } catch {
    // swallow-ok: Only optional missing-header telemetry failed; the request still reaches candidate credential checks and HTTP auth failure handling.
    // best-effort only
  }
}

/** Prepare the signed credential for this request's captured candidate session. */
export function prepareCandidateRequest(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> {
  // Candidate interview app credentials (supports unauthenticated candidate routes).
  // The mint request itself carries its credential in the body.
  // Explicit legacy headers are removed too: no request can downgrade authentication.
  for (const header of [
    'interview-encodedId',
    'interview-encodedTestData',
    'interview-encodedPracticeData',
  ]) {
    config.headers.delete(header);
  }
  if (isCapabilityMintUrl(config.url)) {
    config.url = mapCandidateRequestUrl(
      config.url,
      config.candidateContext ?? captureCandidateContext()
    );
    return config;
  }
  const credential =
    config.candidateContext === undefined
      ? captureCandidateContext()
      : config.candidateContext;
  config.candidateContext = credential;
  if (!credential) {
    maybeLogMissingInterviewHeader(config);
    return config;
  }
  const stored = peekCapability(credential);
  if (stored !== undefined) {
    return applyCandidateCredential(config, credential, stored);
  }
  return ensureCapability(credential).then((token) =>
    applyCandidateCredential(config, credential, token)
  );
}

/** Attach only the signed credential for the request's captured context. */
function applyCandidateCredential(
  config: InternalAxiosRequestConfig,
  credential: CandidateContext,
  token: string | null
): InternalAxiosRequestConfig {
  if (!token)
    throw new Error('The interview session did not provide a credential');
  const session = getCandidateSession(credential);
  if (!session) throw new Error('The interview request has no session');
  config.url = mapCandidateRequestUrl(config.url, credential);
  config.candidateContext = {
    kind: 'session',
    session_id: session.session_id,
    ...(credential.transportId ? { transportId: credential.transportId } : {}),
  };
  setHeader(config, CANDIDATE_CAPABILITY_HEADER, token);
  return config;
}

function isCandidateCapabilityInvalid(error: AxiosError<unknown>): boolean {
  if (error.response?.status !== 401) return false;
  const data: unknown = error.response.data;
  return isRecord(data) && data.detail === CANDIDATE_CAPABILITY_INVALID_DETAIL;
}

/**
 * A request whose capability the server rejected as invalid (expired, bad
 * signature) is retried once after a fresh mint.
 */
export function retryWithFreshCandidateCapability(
  error: AxiosError<unknown>,
  request: (config: AxiosRequestConfig) => Promise<AxiosResponse<unknown>>
): Promise<AxiosResponse<unknown>> | null {
  const config = error.config;
  if (!config || config.candidateCapabilityRetry) return null;
  if (!isCandidateCapabilityInvalid(error)) return null;
  const rejectedToken = readHeader(config.headers, CANDIDATE_CAPABILITY_HEADER);
  if (!rejectedToken) return null;

  const context = config.candidateContext;
  if (!context) return null;
  invalidateCapabilityToken(rejectedToken, context);
  const headers = new AxiosHeaders(config.headers);
  headers.delete(CANDIDATE_CAPABILITY_HEADER);
  // The interceptor refreshes the captured session. Failure propagates without downgrade.
  return request({
    ...config,
    headers,
    candidateCapabilityRetry: true,
  });
}
